import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import {
  Activity, Bug, CheckCircle, Trash2, RefreshCw,
  FileText, Database
} from 'lucide-react';

const SystemHealthPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('db_logs');
  const [sourceFilter, setSourceFilter] = useState('');

  // ================= DATABASE LOGS =================
  const { data: errors = [], refetch: refetchDB, isFetching: isFetchingDB } = useQuery({
    queryKey: ['systemErrors', sourceFilter],
    queryFn: async () => {
      try {
        const res = await axiosClient.get('/admin/system/errors', { params: { source: sourceFilter } });
        return Array.isArray(res) ? res : [];
      } catch {
        return [];
      }
    },
    refetchInterval: 10000,
    enabled: activeTab === 'db_logs'
  });

  // ================= SERVER CRASH LOGS =================
  const {
    data: fileLogs = [],
    refetch: refetchFile,
    isFetching: isFetchingFile
  } = useQuery({
    queryKey: ['fileLogs'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/system/file-logs');
      return res?.logs || [];
    },
    enabled: activeTab === 'file_logs',
    refetchInterval: 5000
  });

  // ================= ERROR VAULT =================
  const {
    data: vaultLogs = [],
    refetch: refetchVault,
    isFetching: isFetchingVault
  } = useQuery({
    queryKey: ['errorVault'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/system/error-vault');
      return res?.logs || [];
    },
    enabled: activeTab === 'error_vault',
    refetchInterval: 5000
  });

  // ================= CLEAR MUTATIONS =================
  const clearDBMutation = useMutation({
    mutationFn: () => axiosClient.delete('/admin/system/errors'),
    onSuccess: () => queryClient.invalidateQueries(['systemErrors'])
  });

  const clearVaultMutation = useMutation({
    mutationFn: () => axiosClient.delete('/admin/system/error-vault'),
    onSuccess: () => {
        queryClient.invalidateQueries(['errorVault']);
        alert("Error Vault cleared!");
    }
    });


  const clearFileMutation = useMutation({
    mutationFn: () => axiosClient.delete('/admin/system/file-logs'),
    onSuccess: () => queryClient.invalidateQueries(['fileLogs'])
  });

  const handleRefresh = () => {
    if (activeTab === 'db_logs') refetchDB();
    if (activeTab === 'file_logs') refetchFile();
    if (activeTab === 'error_vault') refetchVault();
  };

  const isRefreshing = isFetchingDB || isFetchingFile || isFetchingVault;

  return (
    <div className="space-y-6 pb-10">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Activity className="w-7 h-7 mr-2 text-red-600" />
            System Health Monitor
          </h1>
          <p className="text-sm text-slate-500">Centralized error & crash visibility</p>
        </div>

        <button onClick={handleRefresh}
          className="p-2 border rounded-lg bg-white hover:bg-slate-50">
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* TABS */}
      <div className="flex border-b">
        {[
          ['db_logs', 'Database Events', Database],
          ['error_vault', 'Error Vault', Bug],
          ['file_logs', 'Server Crash Logs', FileText]
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-6 py-3 text-sm font-bold flex items-center gap-2 border-b-2
              ${activeTab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ================= DATABASE VIEW ================= */}
      {activeTab === 'db_logs' && (
        <>
          <div className="flex justify-end gap-2">
            <select
              className="p-2 border rounded-lg text-sm"
              onChange={e => setSourceFilter(e.target.value)}
            >
              <option value="">All Sources</option>
              <option value="backend">Backend</option>
              <option value="agent">Agent</option>
              <option value="database">Database</option>
            </select>

            <button
              onClick={() => window.confirm('Clear DB logs?') && clearDBMutation.mutate()}
              className="p-2 bg-red-50 border rounded-lg text-red-600"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          {errors.length === 0 ? (
            <div className="p-12 text-center bg-white border rounded-xl">
              <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
              <p className="mt-2">No database errors</p>
            </div>
          ) : errors.map(err => (
            <div key={err.id} className="bg-white border-l-4 border-red-500 p-4 rounded">
              <div className="font-bold">{err.message}</div>
              <pre className="text-xs text-green-500 mt-2">{err.stack_trace}</pre>
            </div>
          ))}
        </>
      )}

      {/* ================= ERROR VAULT VIEW ================= */}
      {activeTab === 'error_vault' && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => window.confirm('Clear Error Vault?') && clearVaultMutation.mutate()}
              className="p-2 bg-purple-50 border rounded-lg text-purple-600"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-black text-green-400 p-4 h-[600px] overflow-y-auto font-mono text-xs rounded">
            {vaultLogs.length === 0
              ? 'No error vault entries.'
              : vaultLogs.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        </>
      )}

      {/* ================= FILE LOG VIEW ================= */}
      {activeTab === 'file_logs' && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => window.confirm('Clear crash logs?') && clearFileMutation.mutate()}
              className="p-2 bg-red-50 border rounded-lg text-red-600"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-[#0f172a] text-slate-300 p-4 h-[600px] overflow-y-auto font-mono text-xs rounded">
            {fileLogs.length === 0
              ? 'No crash logs.'
              : fileLogs.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </>
      )}
    </div>
  );
};

export default SystemHealthPage;
